import type { Category } from '@avrahami/contracts';

export interface CategoryNode {
  category: Category;
  depth: number;
}

function byPosition(first: Category, second: Category): number {
  return first.position - second.position || first.name.localeCompare(second.name, 'he');
}

export interface CategoryBranch {
  category: Category;
  children: CategoryBranch[];
}

/**
 * The categories as a tree (children by position). A category whose parent is missing, or that
 * sits in a parent cycle (bad data), is shown at the top level, so nothing disappears.
 */
export function categoryForest(categories: readonly Category[]): CategoryBranch[] {
  const ids = new Set(categories.map((category) => category.id));
  const children = new Map<string | null, Category[]>();
  for (const category of categories) {
    const parent =
      category.parentId !== null && ids.has(category.parentId) ? category.parentId : null;
    const list = children.get(parent) ?? [];
    list.push(category);
    children.set(parent, list);
  }
  const seen = new Set<string>();
  const build = (parent: string | null): CategoryBranch[] => {
    const branches: CategoryBranch[] = [];
    for (const category of [...(children.get(parent) ?? [])].sort(byPosition)) {
      if (seen.has(category.id)) continue; // a cycle in the data never loops forever
      seen.add(category.id);
      branches.push({ category, children: build(category.id) });
    }
    return branches;
  };
  const top = build(null);
  // Categories in a parent cycle are reached from no root: list them anyway, at the top.
  for (const category of [...categories].sort(byPosition)) {
    if (seen.has(category.id)) continue;
    seen.add(category.id);
    top.push({ category, children: build(category.id) });
  }
  return top;
}

/** The categories in tree order (each parent followed by its children), with their depth. */
export function categoryTree(categories: readonly Category[]): CategoryNode[] {
  const result: CategoryNode[] = [];
  const walk = (branches: readonly CategoryBranch[], depth: number) => {
    for (const branch of branches) {
      result.push({ category: branch.category, depth });
      walk(branch.children, depth + 1);
    }
  };
  walk(categoryForest(categories), 0);
  return result;
}

/** The ids under a category (children, their children, …). */
export function descendantIds(categories: readonly Category[], id: string): Set<string> {
  const found = new Set<string>();
  const queue = [id];
  while (queue.length > 0) {
    const current = queue.shift();
    for (const category of categories) {
      if (category.parentId === current && !found.has(category.id)) {
        found.add(category.id);
        queue.push(category.id);
      }
    }
  }
  return found;
}

/** Where a category may move: anywhere but itself and its own descendants. */
export function parentOptions(categories: readonly Category[], id: string): CategoryNode[] {
  const excluded = descendantIds(categories, id);
  excluded.add(id);
  return categoryTree(categories).filter((node) => !excluded.has(node.category.id));
}

/** A category's name with its path ("כלי מטבח › סירים"), for pickers. */
export function categoryPath(categories: readonly Category[], id: string): string {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const names: string[] = [];
  let current = byId.get(id);
  const seen = new Set<string>();
  while (current !== undefined && !seen.has(current.id)) {
    seen.add(current.id);
    names.unshift(current.name);
    current = current.parentId === null ? undefined : byId.get(current.parentId);
  }
  return names.join(' › ');
}
